import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { Button } from "../components/ui/button";
import { Plus, ArrowRight } from "lucide-react";

const StatCard = ({ label, value, sub }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4">
    <div className="eyebrow">{label}</div>
    <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [trips, setTrips] = useState([]);
  const [drivers, setDrivers] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/stats").then((r) => setStats(r.data)),
      api.get("/trips").then((r) => setTrips(r.data.slice(0, 5))),
      api.get("/drivers").then((r) => setDrivers(r.data)),
    ]).catch(() => {});
  }, []);

  if (!stats) return <div className="text-slate-500">Loading dashboard...</div>;

  const driverName = (id) => drivers.find((d) => d.driver_id === id)?.name || "—";
  const totalMiles = trips.reduce((s, t) => s + (parseFloat(t.paid_miles) || 0), 0);
  const totalGross = trips.reduce((s, t) => s + (parseFloat(t.gross_amount) || 0), 0);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">Dashboard</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Miles, revenue and driver pay. Every number is one click from its source record.
          </p>
        </div>
        <Button asChild className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="log-trip-btn">
          <Link to="/trips"><Plus size={16} className="mr-1.5" /> Log a trip</Link>
        </Button>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard label="Miles Run" value={totalMiles.toLocaleString()} sub={`${trips.length} recent trips`} />
        <StatCard label="Gross Revenue" value={`$${totalGross.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} sub="Trip billings in period" />
        <StatCard label="Driver Pay" value={`$${stats.total_paid.toLocaleString(undefined, { minimumFractionDigits: 2 })}`} sub="Net pay issued" />
        <StatCard label="Statements" value={stats.statements} sub={`${stats.recent_statements.filter((s) => (s.status || "draft") === "draft").length} drafts`} />
        <StatCard label="Active Drivers" value={stats.active_drivers} sub={`${stats.drivers} total`} />
      </div>

      <div className="flex gap-2 flex-wrap">
        <span className="pill-green">{stats.active_drivers} active drivers</span>
        <span className="pill-gray">{stats.drivers} driver records</span>
        <span className="pill-blue">{stats.statements} pay statements</span>
        {stats.recent_statements.some((s) => (s.status || "draft") === "draft") && (
          <span className="pill-amber">
            {stats.recent_statements.filter((s) => (s.status || "draft") === "draft").length} pay runs not yet marked paid
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent trips */}
        <div className="rounded-lg border border-slate-200 bg-white">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div>
              <div className="eyebrow">Recent trips</div>
              <div className="text-xs text-slate-500">Newest trips in the trip log</div>
            </div>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/trips">Open trip log <ArrowRight size={14} className="ml-1" /></Link>
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50">
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">Driver</th>
                  <th className="px-4 py-2">Route</th>
                  <th className="px-4 py-2 text-right">Miles</th>
                </tr>
              </thead>
              <tbody>
                {trips.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-500 text-sm">No trips yet.</td></tr>
                )}
                {trips.map((t) => (
                  <tr key={t.trip_id} className="border-t border-slate-100">
                    <td className="px-4 py-2.5 font-mono text-xs">{t.load_date}</td>
                    <td className="px-4 py-2.5">{t.driver_ids.map(driverName).join(" & ")}</td>
                    <td className="px-4 py-2.5 text-slate-600">
                      {t.origin_city}, {t.origin_state} → {t.destination_city}, {t.destination_state}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono">{Number(t.paid_miles).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Open pay runs */}
        <div className="rounded-lg border border-slate-200 bg-white">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div>
              <div className="eyebrow">Open pay runs</div>
              <div className="text-xs text-slate-500">Saved pay runs not yet marked as paid</div>
            </div>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/pay-calculator">Pay calculator <ArrowRight size={14} className="ml-1" /></Link>
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50">
                  <th className="px-4 py-2">Driver</th>
                  <th className="px-4 py-2">Period</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2 text-right">Net Pay</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_statements.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-500 text-sm">No pay statements yet.</td></tr>
                )}
                {stats.recent_statements.map((s) => (
                  <tr key={s.statement_id} className="border-t border-slate-100">
                    <td className="px-4 py-2.5">
                      <Link to={`/pay-statements/${s.statement_id}`} className="text-blue-700 hover:underline">
                        {s.driver_name}
                      </Link>
                      <div className="text-[11px] font-mono text-slate-400">{s.statement_number}</div>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{s.period_start} → {s.period_end}</td>
                    <td className="px-4 py-2.5">
                      <span className={(s.status || "draft") === "paid" ? "pill-green" : "pill-amber"}>
                        {(s.status || "draft").toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold">${s.totals.net.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
